import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import generateHeadingId from '../utils/generateHeadingId';
import videoEmbed from '../utils/videoEmbed';

const detectCalloutVariant = (children) => {
  const text = React.Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string') return child;
      if (child.props && child.props.children) {
        return React.Children.toArray(child.props.children)
          .map((c) => (typeof c === 'string' ? c : ''))
          .join('');
      }
      return '';
    })
    .join('');

  if (text.startsWith('Note:')) return 'info';
  if (text.startsWith('Tip:')) return 'tip';
  if (text.startsWith('Warning:')) return 'warning';
  return null;
};

const loneLinkHref = (node) => {
  const elements = (node?.children || []).filter((child) => child.type !== 'text' || child.value.trim() !== '');
  if (elements.length !== 1 || elements[0].tagName !== 'a') return null;
  return elements[0].properties?.href || null;
};

export const ArticleParagraph = ({ node, children }) => {
  const embed = videoEmbed(loneLinkHref(node));
  if (!embed) return <p>{children}</p>;
  if (embed.kind === 'file') {
    return <video className="user-manual-video" src={embed.src} controls preload="metadata" />;
  }
  return (
    <div className="user-manual-video">
      <iframe src={embed.src} title="Video" allow="fullscreen; picture-in-picture" allowFullScreen loading="lazy" />
    </div>
  );
};

export const ArticleImage = ({ src, alt }) => (
  src && (src.startsWith('https://') || src.startsWith('/user_manual/assets/'))
    ? <img className="user-manual-image" src={src} alt={alt || ''} loading="lazy" />
    : <span>{alt}</span>
);

const markdownComponents = {
  p: ArticleParagraph,
  img: ArticleImage,
  h2: ({ children }) => {
    const text = React.Children.toArray(children).join('');
    return <h2 id={generateHeadingId(text)}>{children}</h2>;
  },
  h3: ({ children }) => {
    const text = React.Children.toArray(children).join('');
    return <h3 id={generateHeadingId(text)}>{children}</h3>;
  },
  blockquote: ({ children }) => {
    const variant = detectCalloutVariant(children);
    if (variant) {
      return (
        <div className={`user-manual-callout user-manual-callout-${variant}`}>
          {children}
        </div>
      );
    }
    return <blockquote>{children}</blockquote>;
  },
};

const UserManualArticle = ({ section, chapter, article, prevArticle, nextArticle, onNavigate }) => (
  <div className="user-manual-article">
    <nav className="user-manual-breadcrumb">
      <span>
        {section.title}
        {' \u203A '}
        {chapter.title}
      </span>
    </nav>
    <h2 className="user-manual-article-title">{article.title}</h2>
    {article.locked && (
      <div className="user-manual-callout user-manual-callout-info">
        <p>This feature is not turned on for your account yet. Ask Conductor to contact Client Support, and we will help you get it set up.</p>
      </div>
    )}
    <div className="user-manual-article-body">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{article.body}</ReactMarkdown>
    </div>
    {(prevArticle || nextArticle) && (
      <nav className="user-manual-article-nav">
        {prevArticle ? (
          <a
            href={`#${prevArticle.key}`}
            className="user-manual-article-nav-card"
            onClick={(e) => { e.preventDefault(); onNavigate(prevArticle.key); }}
          >
            <span className="user-manual-article-nav-label">Previous</span>
            <span className="user-manual-article-nav-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              {prevArticle.title}
            </span>
          </a>
        ) : <div />}
        {nextArticle ? (
          <a
            href={`#${nextArticle.key}`}
            className="user-manual-article-nav-card user-manual-article-nav-card-next"
            onClick={(e) => { e.preventDefault(); onNavigate(nextArticle.key); }}
          >
            <span className="user-manual-article-nav-label">Next</span>
            <span className="user-manual-article-nav-title">
              {nextArticle.title}
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </span>
          </a>
        ) : <div />}
      </nav>
    )}
  </div>
);

export default UserManualArticle;
